import {
  ArrowDown,
  ArrowRight,
  ArrowUpRight,
  BookOpen,
  Flame,
  HeartHandshake,
  Megaphone,
  Palette,
  Sparkles,
  Users,
  Zap,
} from "lucide-react";
import Header from "@/reusable_sections/Header";
import Footer from "@/reusable_sections/Footer";
import { assetUrl, homeUrl } from "@/lib/assets";

const movements = [
  {
    number: "01",
    title: "Conectamos",
    description:
      "Construimos una red donde pastores, líderes y jóvenes avanzan acompañados, comparten experiencias y sirven juntos.",
    icon: Users,
  },
  {
    number: "02",
    title: "Formamos",
    description:
      "Impulsamos una fe bíblica, profunda y práctica que ayude a cada joven a descubrir su identidad y desarrollar sus dones.",
    icon: BookOpen,
  },
  {
    number: "03",
    title: "Movilizamos",
    description:
      "Activamos a la próxima generación para compartir a Jesús, responder a las necesidades de su comunidad y vivir con propósito.",
    icon: Zap,
  },
];

const initiatives = [
  {
    kicker: "Fe que echa raíces",
    title: "Compromiso Bíblico",
    description:
      "Recursos y experiencias que acercan a los jóvenes a la Palabra y los ayudan a llevarla a su vida cotidiana.",
    image: "youth_bible_quiz.png",
    alt: "Jóvenes estudiando juntos al aire libre",
    icon: BookOpen,
  },
  {
    kicker: "Dones al servicio de Dios",
    title: "Bellas Artes",
    description:
      "Un espacio para cultivar la creatividad, desarrollar talentos y expresar la fe a través del arte.",
    image: "youth_fine_arts.webp",
    alt: "Joven desarrollando una expresión artística",
    icon: Palette,
  },
  {
    kicker: "Generosidad que llega lejos",
    title: "Speed the Light",
    description:
      "Movilizamos a los jóvenes para apoyar la misión y responder a necesidades ministeriales dentro y fuera del país.",
    image: "youth_speed_the_light.png",
    alt: "Jóvenes participando en una iniciativa misionera",
    icon: Flame,
  },
];

const contactUrl = homeUrl("contacto/?tema=red-pastoral#contacto-formulario");

export default function RedPastoralYouthPage() {
  return (
    <div className="acad-react-page acad-rpj-page">
      <Header />

      <main>
        <section className="acad-rpj-hero" data-nav="dark">
          <img
            className="acad-rpj-hero-image"
            src={assetUrl("youth_ministry_worship.png")}
            alt="Jóvenes reunidos en una celebración de adoración"
            loading="eager"
            decoding="async"
          />
          <span className="acad-rpj-hero-overlay" />
          <span className="acad-rpj-hero-grid" aria-hidden="true" />

          <div className="acad-rpj-shell acad-rpj-hero-inner">
            <div className="acad-rpj-hero-topline">
              <span className="acad-rpj-mini-mark">RPJ</span>
              <span>Red Pastoral Juvenil · Costa Rica</span>
            </div>

            <div className="acad-rpj-hero-copy">
              <span className="acad-rpj-eyebrow">Jesús en el centro</span>
              <h1>
                Una generación
                <span>en <em>movimiento.</em></span>
              </h1>
              <p>
                Conectamos, formamos y movilizamos a la próxima generación para vivir
                una fe auténtica y transformar Costa Rica.
              </p>
            </div>

            <div className="acad-rpj-hero-bottom">
              <a className="acad-rpj-button" href="#nuestra-red">
                Conocer la red <ArrowDown aria-hidden="true" />
              </a>
              <div className="acad-rpj-hero-tags" aria-label="Enfoques de la red">
                <span>Fe viva</span>
                <span>Comunidad</span>
                <span>Misión</span>
              </div>
            </div>
          </div>

          <span className="acad-rpj-hero-word" aria-hidden="true">YOUTH</span>
        </section>

        <div className="acad-rpj-marquee" aria-label="Identidad de la Red Pastoral Juvenil">
          <div>
            <span>Conectar</span><i />
            <span>Formar</span><i />
            <span>Movilizar</span><i />
            <span>Una generación con propósito</span><i />
            <span>Conectar</span><i />
            <span>Formar</span><i />
            <span>Movilizar</span><i />
            <span>Una generación con propósito</span><i />
          </div>
        </div>

        <section className="acad-rpj-intro" id="nuestra-red">
          <div className="acad-rpj-shell acad-rpj-intro-grid">
            <div className="acad-rpj-intro-copy">
              <span className="acad-rpj-eyebrow">Somos la red</span>
              <h2>No somos el futuro. Somos parte de lo que Dios está haciendo hoy.</h2>
              <p>
                Somos el ministerio juvenil de las Asambleas de Dios de Costa Rica. Caminamos
                al lado de pastores, líderes y jóvenes para crear comunidades seguras donde
                cada persona pueda encontrar a Jesús, descubrir su propósito y crecer con
                fundamentos firmes.
              </p>
              <p>
                Creemos en una generación llena del Espíritu, comprometida con la Palabra y
                dispuesta a servir a su iglesia, su comunidad y su país.
              </p>
              <a className="acad-rpj-text-link" href="#como-avanzamos">
                Así avanzamos <ArrowDown aria-hidden="true" />
              </a>
            </div>

            <div className="acad-rpj-intro-media">
              <img
                src={assetUrl("Ministry_Youth.jpg")}
                alt="Grupo de jóvenes compartiendo en comunidad"
                loading="lazy"
                decoding="async"
              />
              <div className="acad-rpj-sticker">
                <Sparkles aria-hidden="true" />
                <span>Una red.<br />Muchos llamados.</span>
              </div>
              <span className="acad-rpj-media-label">Jóvenes · Líderes · Pastores</span>
            </div>
          </div>
        </section>

        <section className="acad-rpj-movements" id="como-avanzamos">
          <div className="acad-rpj-shell">
            <header className="acad-rpj-section-head">
              <span className="acad-rpj-eyebrow">Nuestro pulso</span>
              <h2>Tres acciones.<br />Una misma misión.</h2>
            </header>

            <div className="acad-rpj-movement-grid">
              {movements.map((movement) => {
                const Icon = movement.icon;

                return (
                  <article className="acad-rpj-movement" key={movement.number}>
                    <div className="acad-rpj-movement-top">
                      <span>{movement.number}</span>
                      <i><Icon aria-hidden="true" /></i>
                    </div>
                    <h3>{movement.title}</h3>
                    <p>{movement.description}</p>
                    <ArrowRight className="acad-rpj-movement-arrow" aria-hidden="true" />
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="acad-rpj-initiatives" id="iniciativas">
          <div className="acad-rpj-shell">
            <header className="acad-rpj-initiatives-head">
              <div>
                <span className="acad-rpj-eyebrow">La red en acción</span>
                <h2>Experiencias que despiertan propósito.</h2>
              </div>
              <p>
                Herramientas para profundizar la fe, desarrollar dones y participar
                activamente en la misión de Dios.
              </p>
            </header>

            <div className="acad-rpj-initiative-grid">
              {initiatives.map((initiative, index) => {
                const Icon = initiative.icon;

                return (
                  <article className="acad-rpj-initiative" key={initiative.title}>
                    <div className="acad-rpj-initiative-media">
                      <img
                        src={assetUrl(initiative.image)}
                        alt={initiative.alt}
                        loading="lazy"
                        decoding="async"
                      />
                      <span>{String(index + 1).padStart(2, "0")}</span>
                    </div>
                    <div className="acad-rpj-initiative-copy">
                      <div className="acad-rpj-initiative-kicker">
                        <i><Icon aria-hidden="true" /></i>
                        <span>{initiative.kicker}</span>
                      </div>
                      <h3>{initiative.title}</h3>
                      <p>{initiative.description}</p>
                      <a href={contactUrl}>
                        Quiero saber más <ArrowUpRight aria-hidden="true" />
                      </a>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="acad-rpj-manifesto">
          <img
            src={assetUrl("youth_ministry_worship.png")}
            alt="Jóvenes adorando juntos"
            loading="lazy"
            decoding="async"
          />
          <span className="acad-rpj-manifesto-overlay" />
          <div className="acad-rpj-shell acad-rpj-manifesto-inner">
            <span className="acad-rpj-manifesto-mark">RPJ</span>
            <blockquote>
              Jesús en el centro.<br />
              <em>La próxima generación en acción.</em>
            </blockquote>
            <div>
              <span><HeartHandshake aria-hidden="true" /> Pertenecer</span>
              <span><Flame aria-hidden="true" /> Crecer</span>
              <span><Megaphone aria-hidden="true" /> Compartir</span>
            </div>
          </div>
        </section>

        <section className="acad-rpj-final">
          <div className="acad-rpj-shell acad-rpj-final-inner">
            <div>
              <span className="acad-rpj-eyebrow">Hay un lugar para ti</span>
              <h2>Conecta tu iglesia con la red.</h2>
              <p>
                Escríbenos para conocer actividades, recursos y oportunidades de participación.
              </p>
            </div>
            <a href={contactUrl}>
              Hablemos <ArrowUpRight aria-hidden="true" />
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
